// Side panels of the Preschool invoices page: council funding reconciliation per term, and children not invoiced yet.

import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatPounds, today, type FundingPayment, type PreschoolInvoice, type PreschoolTerm } from "../../shared/preschoolInvoices";
import { NotInvoiced, totals } from "./list";
import { shortDate } from "./InvoiceRow";

const money = (pence: number) => `£${formatPounds(pence)}`;

const Expander: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <IconButton size="small" onClick={onClick} sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }}>
    <ExpandMoreIcon />
  </IconButton>
);

/** Council funding per term: expected from the term's invoices (funded hours x rate) against the lump sums received. */
export const FundingPanel: React.FC<{
  terms: PreschoolTerm[];
  invoices: PreschoolInvoice[];
  payments: FundingPayment[];
  onAdd: (payment: Omit<FundingPayment, "id">) => Promise<void>;
  onDelete: (payment: FundingPayment) => void;
}> = ({ terms, invoices, payments, onAdd, onDelete }) => {
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ termId: string; date: string; amount: string; note: string } | null>(null);
  const rows = terms.map((term) => {
    const termPayments = payments.filter((p) => p.termId === term.id).sort((a, b) => a.date.localeCompare(b.date));
    return { term, payments: termPayments, ...totals(invoices.filter((i) => i.termId === term.id), termPayments) };
  }).filter((row) => row.fundingExpectedPence > 0 || row.payments.length > 0);

  const amount = adding ? Math.round(Number(adding.amount) * 100) : NaN;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>Council funding</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Funded hours × rate, against the lump sums received</Typography>
      {rows.length === 0 && <Typography variant="body2" color="text.secondary">No funded hours in this view.</Typography>}
      <Stack spacing={1.5}>
        {rows.map((row) => {
          const share = row.fundingExpectedPence ? Math.min(100, (row.fundingReceivedPence / row.fundingExpectedPence) * 100) : 100;
          const due = row.fundingExpectedPence - row.fundingReceivedPence;
          return (
            <Box key={row.term.id}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{row.term.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {money(row.fundingReceivedPence)} of {money(row.fundingExpectedPence)}
                    {due > 0 ? ` · ${money(due)} to come` : due < 0 ? ` · ${money(-due)} more than expected` : " · settled"}
                  </Typography>
                </Box>
                <Tooltip title="Add a payment">
                  <IconButton size="small" onClick={() => setAdding({ termId: row.term.id, date: today(), amount: due > 0 ? formatPounds(due) : "", note: "" })}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Expander open={open === row.term.id} onClick={() => setOpen(open === row.term.id ? null : row.term.id)} />
              </Stack>
              <LinearProgress variant="determinate" value={share} color={due <= 0 ? "success" : "primary"} sx={{ mt: 0.5, height: 5, borderRadius: 3 }} />
              <Collapse in={open === row.term.id} unmountOnExit>
                <Stack sx={{ mt: 1 }}>
                  {row.payments.length === 0 && <Typography variant="caption" color="text.secondary">No payments yet.</Typography>}
                  {row.payments.map((p) => (
                    <Stack key={p.id} direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ width: 72 }}>{shortDate(p.date)}</Typography>
                      <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{p.note}</Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(p.amountPence)}</Typography>
                      <IconButton size="small" aria-label="Delete payment" onClick={() => onDelete(p)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>

      <Dialog open={!!adding} onClose={() => setAdding(null)} fullWidth maxWidth="xs">
        <DialogTitle>Funding received · {terms.find((t) => t.id === adding?.termId)?.name}</DialogTitle>
        {adding && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Date received" type="date" value={adding.date} InputLabelProps={{ shrink: true }}
                onChange={(e) => setAdding({ ...adding, date: e.target.value })} />
              <TextField label="Amount" value={adding.amount} inputProps={{ inputMode: "decimal" }} error={!(amount > 0)}
                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                onChange={(e) => setAdding({ ...adding, amount: e.target.value })} />
              <TextField label="Note" placeholder="e.g. Bucks Council, 2nd instalment" value={adding.note}
                onChange={(e) => setAdding({ ...adding, note: e.target.value })} />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setAdding(null)}>Cancel</Button>
          <Button variant="contained" disabled={!adding || !(amount > 0) || !adding.date}
            onClick={async () => {
              const { termId, date, note } = adding!;
              setAdding(null);
              await onAdd({ termId, date, amountPence: amount, note: note.trim() });
            }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

/** Active children with no childcare invoice in the current term. */
export const NotInvoicedPanel: React.FC<{
  term: PreschoolTerm;
  items: NotInvoiced[];
  onCreate: (item: NotInvoiced) => void;
}> = ({ term, items, onCreate }) => {
  const [open, setOpen] = useState(true);
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: items.length ? "warning.light" : undefined }}>
      <Stack direction="row" alignItems="center">
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Not invoiced · {term.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {items.length ? `${items.length} child${items.length === 1 ? "" : "ren"} without a childcare invoice this term` : "Every child has an invoice this term"}
          </Typography>
        </Box>
        {items.length > 0 && <Expander open={open} onClick={() => setOpen(!open)} />}
      </Stack>
      <Collapse in={open && items.length > 0} unmountOnExit>
        <Stack sx={{ mt: 1 }} divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
          {items.map((item) => (
            <Stack key={item.child.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.75 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>{item.child.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {item.previous ? `Last ${item.previous.ref} · ${item.previous.period}` : "No earlier invoice"}
                </Typography>
              </Box>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onCreate(item)}>Create</Button>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
};
